import * as React from "react";
import { DateTime } from "luxon";
import { Helmet } from "react-helmet";
import { Link } from "found";
import { ThemeContextConsumer } from "../../application/themeContext";
import { filter } from "graphql-anywhere";
import { getLogoForBrandUrl } from "../../helpers";
import { makeStyles } from "@mui/styles";
import { fragments as productionMethodIconSetFragments } from "../../components/ProductionMethodIconSet/graph";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import CustomerStatusChip from "../../../src/application/apps/OrderApp/components/HeaderToolbar/components/CustomerStatusChip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import NoBrandTagIcon from "../../assets/no-brand-tag.png";
import Paper from "@mui/material/Paper";
import PriorityChip from "../../components/PriorityChip";
import ProductionMethodIconSet from "../../components/ProductionMethodIconSet";
import ProductionStatusLabel from "../../components/ProductionStatusLabel";
import SnackbarContent from "@mui/material/SnackbarContent";
import Typography from "@mui/material/Typography";
import WarningIcon from "@mui/icons-material/Warning";

import { useMemo, useState } from "react";
import OrderProductionJobEmbroiderySplitWarning from "../OrderProductionJobEmbroiderySplitWarning";
import pluralize from "pluralize";

const useStyles = makeStyles(theme => ({
  brandLogo: {
    width: 15
  },
  container: {
    display: "flex",
    flex: "1 1 100%"
  },

  closeIconContainer: {
    marginLeft: "auto"
  },

  customerArchiveLink: {
    color: theme.palette.getContrastText(theme.palette.grey[100]),
    fontWeight: theme.typography.fontWeightMedium,
    textDecoration: "underline",
    textTransform: "none"
  },

  divider: {
    width: 1,
    backgroundColor: theme.palette.grey[500],
    margin: theme.spacing(3)
  },

  flexContainer: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },

  logo: {
    width: 20,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    height: 20
  },

  orderStatusView: {
    marginTop: theme.spacing(1)
  },

  paperContainer: {
    color: theme.palette.mode === "dark" && "#fff",
    backgroundColor: "inherit",
    margin: 0,
    padding: theme.spacing(2)
  },

  resolutionSnackbar: {
    backgroundColor: "#B39DDB",
    maxWidth: "100%"
  },

  resolutionSnackbarIcon: {
    fontSize: 20,
    marginRight: theme.spacing(1),
    color: "#000000"
  },

  resolutionSnackbarMessage: {
    display: "flex",
    alignItems: "center",
    color: "#000000"
  },

  specialtySnackbar: {
    backgroundColor: "#ca65f2ff",
    maxWidth: "100%"
  },

  specialtySnackbarIcon: {
    fontSize: 20,
    marginRight: theme.spacing(1),
    color: "#000000"
  },

  specialtySnackbarMessage: {
    display: "flex",
    alignItems: "center",
    color: "#000000"
  },

  canceledDialog: {
    color: "#ffffff",
    backgroundColor: theme.palette.error.dark,
    maxWidth: "100%"
  },

  canceledSnackbar: {
    backgroundColor: theme.palette.error.dark,
    maxWidth: "100%"
  },

  canceledSnackbarIcon: {
    fontSize: 20,
    marginRight: theme.spacing(1)
  },

  canceledSnackbarMessage: {
    display: "flex",
    alignItems: "center"
  },

  artworkNotApprovedSnackbar: {
    backgroundColor: theme.palette.error.dark,
    maxWidth: "100%"
  },

  artworkNotApprovedSnackbarIcon: {
    fontSize: 20,
    marginRight: theme.spacing(1)
  },

  artworkNotApprovedSnackbarMessage: {
    display: "flex",
    alignItems: "center"
  },

  notInHouseSnackbar: {
    backgroundColor: theme.palette.error.dark,
    maxWidth: "100%"
  },

  notInHouseSnackbarIcon: {
    fontSize: 20,
    marginRight: theme.spacing(1)
  },

  notInHouseSnackbarMessage: {
    display: "flex",
    alignItems: "center"
  },

  spaceBetweenContainer: {
    display: "flex",
    justifyContent: "space-between"
  },

  spacerRight: {
    marginRight: theme.spacing(1)
  }
}));

const OrderStatusHeader = ({
  order,
  isArtApp,
  order: {
    dueBy,
    id,
    isArtworkApproved,
    isCanceled,
    isInHouse,
    isResolution,
    orderNumber,
    orderProductionJobEmbroiderySplit,
    primaryCustomer: {
      businessUnit,
      insideBrandTagging,
      name_orderStatusAppBar,
      totalOrders,
      status,
      emailAddress,
      primaryPhoneNumber
    },
    priority,
    productionDate,
    productionNotesCollection,
    productionStationAssignments,
    salesRepresentatives,
    stockStatusLabel,
    vendor
  },
  orderStatusView,
  onRequestClose
}) => {
  const classes = useStyles();

  const [cancelDialogIsOpen, setCancelDialogIsOpen] = useState(isCanceled);

  const handleCancelDialogClose = () => {
    setCancelDialogIsOpen(false);
  };

  const assigneesList = useMemo(() => {
    return productionStationAssignments.map(
      assignment =>
        `${assignment.employee.employee_orderStatusAppBar}${
          assignment.productionStation
            ? ` - ${assignment.productionStation.name}`
            : ""
        }`
    );
  }, [productionStationAssignments]);

  const dueDate = useMemo(() => {
    return DateTime.fromISO(dueBy.date).toLocaleString(DateTime.DATE_SHORT);
  }, [dueBy]);

  const brandUrl = useMemo(() => {
    return businessUnit.name;
  }, [businessUnit]);

  const salesRep = useMemo(() => {
    return salesRepresentatives.map(
      salesRepresentative =>
        salesRepresentative.salesRepresentative_orderStatusAppBar
    );
  }, [salesRepresentatives]);

  const isSpecialty = productionNotesCollection && productionNotesCollection.some(({ note }) => note && note.toLowerCase().includes("specialty"));

  return (
    <React.Fragment>
      <Paper square={true} className={classes.paperContainer}>
        <Helmet>
          <title>
            {name_orderStatusAppBar} - {orderNumber}
          </title>
        </Helmet>
        {isResolution && (
          <SnackbarContent
            className={classes.resolutionSnackbar}
            message={
              <span className={classes.resolutionSnackbarMessage}>
                <WarningIcon className={classes.resolutionSnackbarIcon} />
                Resolution
              </span>
            }
          />
        )}
        {isSpecialty && (
          <SnackbarContent
            className={classes.specialtySnackbar}
            message={
              <span className={classes.specialtySnackbarMessage}>
                <WarningIcon className={classes.specialtySnackbarIcon} />
                Specialty
              </span>
            }
          />
        )}
        {isCanceled && (
          <SnackbarContent
            className={classes.canceledSnackbar}
            message={
              <span className={classes.canceledSnackbarMessage}>
                <WarningIcon className={classes.canceledSnackbarIcon} />
                ORDER CANCELLED - DO NOT PRINT & GIVE GOODS TO MANAGER FOR
                RETURN
              </span>
            }
          />
        )}
        <OrderProductionJobEmbroiderySplitWarning
          orderProductionJobEmbroiderySplit={orderProductionJobEmbroiderySplit}
        />
        {!isArtworkApproved && (
          <SnackbarContent
            className={classes.artworkNotApprovedSnackbar}
            message={
              <span className={classes.artworkNotApprovedSnackbar}>
                <WarningIcon
                  className={classes.artworkNotApprovedSnackbarIcon}
                />
                Artwork is NOT approved! Do Not Print!!!
              </span>
            }
          />
        )}
        {!isCanceled && !isInHouse && !vendor.internal && (
          <SnackbarContent
            className={classes.notInHouseSnackbar}
            message={
              <span className={classes.notInHouseSnackbarMessage}>
                <WarningIcon className={classes.notInHouseSnackbarIcon} />
                Order Assigned to {vendor.organization.name}! Do Not Print!!!
              </span>
            }
          />
        )}
        {!isCanceled && !isInHouse && vendor.internal && (
          <SnackbarContent
            message={<span>Order Assigned to {vendor.organization.name}</span>}
          />
        )}
        <div>
          <div className={classes.container}>
            <div>
              <dl className={classes.flexContainer}>
                <div
                  style={{
                    height: "34px",
                    maxWidth: "95px",
                    marginRight: "8px"
                  }}
                >
                  <ThemeContextConsumer>
                    {context => (
                      <img
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%"
                        }}
                        src={getLogoForBrandUrl(
                          brandUrl,
                          context && context.type
                        )}
                        alt={brandUrl}
                      />
                    )}
                  </ThemeContextConsumer>
                </div>
                <CustomerStatusChip status={status} />
                {!insideBrandTagging && (
                  <img
                    style={{ width: "36px", margin: "0 8px" }}
                    src={NoBrandTagIcon}
                    alt="No Brand Tagging"
                  />
                )}
              </dl>
              <dl className={classes.flexContainer}>
                <Typography
                  className={classes.spacerRight}
                  component="dt"
                  variant="h6"
                  color="inherit"
                >
                  {`${name_orderStatusAppBar} (${totalOrders}) `}
                </Typography>
              </dl>
              {isArtApp && (
                <React.Fragment>
                  {primaryPhoneNumber && primaryPhoneNumber.formatted && (
                    <Typography
                      className={classes.spacerRight}
                      variant="subtitle2"
                      color="inherit"
                    >
                      {`Tel: `}
                      <Link
                        to={`tel:+${primaryPhoneNumber.formatted}`}
                        target="_blank"
                        rel="noopener"
                      >
                        {primaryPhoneNumber.formatted}
                      </Link>
                    </Typography>
                  )}
                  {emailAddress && (
                    <Typography
                      className={classes.spacerRight}
                      variant="subtitle2"
                      color="inherit"
                    >
                      {`Email: `}
                      <Link
                        to={`mailto:${emailAddress}`}
                        target="_blank"
                        rel="noopener"
                      >
                        {emailAddress}
                      </Link>
                    </Typography>
                  )}
                </React.Fragment>
              )}
            </div>
            <div className={classes.divider} />
            <div>
              <dl className={classes.flexContainer}>
                <Typography
                  style={{ marginRight: "8px" }}
                  className={classes.spacerRight}
                  component="dt"
                  variant="body2"
                  color="inherit"
                >
                  {orderNumber}
                  <Typography style={{ fontSize: "0.650rem" }}>
                    {pluralize("designs", order.productionJobs.length, true)}
                  </Typography>
                </Typography>
                <ProductionMethodIconSet
                  productionMethodStatuses={filter(
                    productionMethodIconSetFragments.order,
                    order
                  )}
                />
                <Button
                  className={classes.customerArchiveLink}
                  href={`https://crm2.legacy.printfly.com/app#/research/orders/5474309${id}`}
                  target="_blank"
                  rel="noopener"
                >
                  <Typography variant="body2">Research Center</Typography>
                </Button>
              </dl>
              <dl className={classes.flexContainer}>
                <Typography
                  className={classes.spacerRight}
                  component="dt"
                  variant="body2"
                >
                  Production Status:
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  color="textSecondary"
                >
                  <ProductionStatusLabel order={order} />
                </Typography>
              </dl>
              <dl className={classes.flexContainer}>
                <Typography
                  className={classes.spacerRight}
                  component="dt"
                  variant="body2"
                >
                  Assigned:
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  color="textSecondary"
                >
                  {assigneesList.join(", ")}
                </Typography>
              </dl>
            </div>
            <div className={classes.divider} />
            <div>
              <dl className={classes.flexContainer}>
                <Typography
                  className={classes.spacerRight}
                  component="dt"
                  variant="body2"
                >
                  Production Date:
                </Typography>

                <PriorityChip date={productionDate} priority={priority} />
              </dl>
              <dl className={classes.flexContainer}>
                <Typography
                  className={classes.spacerRight}
                  component="dt"
                  variant="body2"
                >
                  Due Date:
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  color="textSecondary"
                >
                  {dueDate}
                </Typography>
              </dl>
            </div>
            <div className={classes.divider} />
            <div>
              <dl className={classes.flexContainer}>
                <Typography
                  className={classes.spacerRight}
                  component="dt"
                  variant="body2"
                >
                  Stock Status:
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  color="textSecondary"
                >
                  {stockStatusLabel}
                </Typography>
              </dl>
              <dl className={classes.flexContainer}>
                <Typography
                  className={classes.spacerRight}
                  component="dt"
                  variant="body2"
                >
                  Sales Rep:
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  color="textSecondary"
                >
                  {salesRep}
                </Typography>
              </dl>
            </div>
            <div className={classes.divider} />
            <div className={classes.orderStatusView}>{orderStatusView}</div>
            <div className={classes.divider} />
            <div className={classes.orderStatusView}>
              <Button
                component="a"
                href={`/expediting-label/order/${order.id}`}
                target="_blank"
                rel="noopener"
                color="secondary"
                variant="contained"
              >
                Print Staging Label
              </Button>
            </div>
            <div className={classes.closeIconContainer}>
              <IconButton
                onClick={() => onRequestClose()}
                aria-label="Close"
                color="inherit"
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </div>
      </Paper>
      <Dialog
        maxWidth="sm"
        open={cancelDialogIsOpen}
        onClose={handleCancelDialogClose}
        PaperProps={{
          className: classes.canceledDialog
        }}
      >
        <DialogTitle>CANCELLED ORDER</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do not print this order as it has been cancelled. Give the goods to
            your manager immediately and let them know it has been cancelled.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default OrderStatusHeader;
